import { useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../shared/axiosInstance";
import { API } from "../../constants";
import { isRejectedAlert } from "./financeAlerts.parse";
import { financeAlertKeys, TFinanceAlertPages } from "./financeAlerts.queries";
import { IFinanceAlert, IFinanceAlertSummary } from "./financeAlerts.types";

export const markFinanceAlertRead = async (id: string) => {
  const res = await axiosInstance.put(
    `${process.env.REACT_APP_BASE_URL}/${API.FINANCE_ALERTS.buildReadUrl(id)}`
  );
  return res.data;
};

export const useMarkFinanceAlertReadMutation = (recipientId: string | null) => {
  const queryClient = useQueryClient();
  const listKey = financeAlertKeys.list(recipientId ?? "");
  const summaryKey = financeAlertKeys.summary(recipientId ?? "");

  return useMutation({
    mutationFn: (alert: IFinanceAlert) => markFinanceAlertRead(alert.id),
    onMutate: async (alert) => {
      await queryClient.cancelQueries({ queryKey: summaryKey });
      const previousList = queryClient.getQueryData<TFinanceAlertPages>(listKey);
      const previousSummary = queryClient.getQueryData<IFinanceAlertSummary>(summaryKey);

      queryClient.setQueryData<TFinanceAlertPages>(listKey, (current) =>
        current
          ? {
              ...current,
              pages: current.pages.map((page) => ({
                ...page,
                data: (page?.data ?? []).map((item) =>
                  item.id === alert.id ? { ...item, read: true } : item
                ),
              })),
            }
          : current
      );

      queryClient.setQueryData<IFinanceAlertSummary>(summaryKey, (current) =>
        current
          ? {
              ...current,
              unread: Math.max(0, current.unread - 1),
              needsAttention: isRejectedAlert(alert)
                ? Math.max(0, current.needsAttention - 1)
                : current.needsAttention,
            }
          : current
      );

      return { previousList, previousSummary };
    },
    onError: (_err, _alert, context) => {
      if (context?.previousList) queryClient.setQueryData(listKey, context.previousList);
      if (context?.previousSummary) {
        queryClient.setQueryData(summaryKey, context.previousSummary);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: summaryKey }),
  });
};
